// src/screens/Galeria/Galeria.js
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import client from '../../sanity/SanityClient';
import imageUrlBuilder from '@sanity/image-url';
import CarruselCard from '../../components/CarruselCard/CarruselCard';
import { Helmet } from "react-helmet";
import './Galeria.css';

const SITE = "https://juanipavlovsky.com.ar";

// Sanity builder
const builder = imageUrlBuilder(client);
const urlFor = (source) => builder.image(source);

// Normalizador (agrega ratio para saber si es horizontal/vertical)
function normalize(docs) {
  return (docs || [])
    .map((d) => {
      const asset = d.previewImage?.asset || d.detalleImage?.asset;
      const src =
        asset?.url ||
        (d.previewImage ? urlFor(d.previewImage).width(1200).auto('format').url() : null);
      if (!src) return null;

      const dims =
        d.previewImage?.asset?.metadata?.dimensions ||
        d.detalleImage?.asset?.metadata?.dimensions ||
        null;
      const ratio = typeof dims?.aspectRatio === 'number' ? dims.aspectRatio : null;

      return {
        id: d._id,
        src,
        alt: d.titulo || '',
        detailRoute: `/detalle/${d._id}`,
        ratio, // ⬅️ lo usará el carrusel para filtrar horizontales
      };
    })
    .filter(Boolean);
}


// Consulta por categoría (sin filtrar subcategoría)
function queryFor(categoriaKey) {
  return `
    *[
      _type == "foto" &&
      !(_id in path("drafts.**")) &&
      (
        lower(categoria) == "${categoriaKey}" ||
        lower(categoria) match "${categoriaKey.replace(/([.*+?^${}()|[\]\\])/g, '\\$1')}*"
      )
    ]|order(_createdAt desc)[0...60]{
      _id, _createdAt, titulo, subcategoria,
      previewImage{asset->{url, metadata{dimensions{width,height,aspectRatio}}}},
      detalleImage{asset->{url, metadata{dimensions{width,height,aspectRatio}}}}
    }
  `;
}

const CATEGORIES = [
  { title: 'Abstractas', categoria: 'abstractas', route: '/galeria/abstractas' },
  { title: 'Almas Visibles', categoria: 'almasvisibles', route: '/galeria/almas-visibles' },
  { title: 'Paisajes Urbanos', categoria: 'paisajesurbanos', route: '/galeria/paisajes-urbanos' },
  { title: 'Entorno Natural', categoria: 'entornonatural', route: '/galeria/entorno-natural' },
];

export default function Galeria() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [data, setData] = useState({}); // categoriaKey -> items[]

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const promises = CATEGORIES.map(async (c) => {
          const res = await client.fetch(queryFor(c.categoria));
          return { key: c.categoria, items: normalize(res) };
        });
        const all = await Promise.all(promises);
        if (!alive) return;
        const map = {};
        all.forEach(({ key, items }) => { map[key] = items; });
        setData(map);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setErr(e?.message || 'Error al cargar');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const sections = useMemo(() => {
    return CATEGORIES.map(c => ({
      ...c,
      items: data[c.categoria] || [],
    }));
  }, [data]);

  // ⛔️ BLOQUEO de click derecho (para toda la vista de Galería)
  const preventContextMenu = (e) => e.preventDefault();

  return (
    <>
      <Helmet>
        <title>Galería</title>
        <meta name="description" content="Explorá series y colecciones de fotografías." />
        <link rel="canonical" href={`${SITE}/galeria`} />
        <meta property="og:title" content="Galería | Juani Pavlovsky" />
        <meta property="og:url" content={`${SITE}/galeria`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": `${SITE}/` },
            { "@type": "ListItem", "position": 2, "name": "Galería", "item": `${SITE}/galeria` }
          ]
        })}</script>

      </Helmet>
      <div
        className="galeria-container"
        onContextMenu={preventContextMenu} // <- evita guardar con click derecho
      >
        <header className="galeria-header">
          <h1 className="galeria-title">Galería</h1>
          <p className="galeria-subtitle">Explorá las categorías</p>
        </header>

        {loading ? (
          <p className="galeria-info">Cargando…</p>
        ) : err ? (
          <p className="galeria-info">Ups, algo salió mal: {err}</p>
        ) : (
          sections.map((cat) => (
            <CarruselCard
              key={cat.categoria}
              title={cat.title}
              items={cat.items}
              categoryRoute={cat.route}
            /* ✅ Para que el carrusel avance de a 1, asegurate de que
               CarruselCard use incrementos de ±1 en sus flechas (handlePrev/Next).
               No hace falta pasar prop: el comportamiento está en CarruselCard. */
            />
          ))
        )}
      </div>
    </>
  );
}
